import clsx from 'clsx';

import Loader from './Loader';

const PADDINGS = {
  none: '',
  xs: 'px-1 py-0.5',
  sm: 'px-2 py-1',
  default: 'px-3 py-2',
  lg: 'px-6 py-2',
  xl: 'px-8 py-3',
  '2xl': 'px-12 py-4',
};

const PADDINGS_FULL_WIDTH = {
  none: 'w-full',
  xs: 'w-full py-0.5',
  sm: 'w-full py-1',
  default: 'w-full py-2',
  lg: 'w-full py-2',
  xl: 'w-full py-3',
  '2xl': 'w-full py-4',
};

type Colors = 'primary' | 'white' | 'gray' | 'action';
type Variants = 'basic' | 'outline' | 'link';

type VariantColors = {
  [variant in Variants]: {
    [color in Colors]: string;
  };
};

const DISABLED_COLORS: { [color in Colors]: string } = {
  primary: '!bg-primary-filled-disabled text-white-faded-default',
  white: '!bg-secondary-filled-disabled text-black-faded-default',
  gray: '!bg-out-light-gray text-black-faded-default',
  action: '',
};

const VARIANTS_COLORS: VariantColors = {
  basic: {
    primary:
      'border border-primary-filled-default bg-primary-filled-default hover:bg-primary-filled-hover rounded-md text-white-filled-default hover:border-primary-filled-hover',
    white:
      'border border-white-filled-default bg-white-filled-default hover:bg-white-filled-hover rounded-md text-black-filled-default hover:border-white-filled-hover',
    gray: '', // Unused
    action: '', // Unused
  },
  outline: {
    primary:
      'border border-primary-filled-default rounded-md text-primary-filled-default hover:bg-primary-50',
    white:
      'border border-secondary-600 rounded-md text-secondary-600 hover:bg-secondary-50',
    gray: 'border border-out-light-gray rounded-md text-out-light-gray hover:bg-out-light-gray/[0.10]',
    action:
      'border border-neutral-faded-default rounded-md text-neutral-faded-default hover:bg-primary-filled-disabled/[0.10] hover:border-action-content-primary-hover',
  },
  link: {
    primary: 'text-primary-filled-default hover:underline',
    white: 'text-secondary-600 hover:underline',
    gray: '', // Unused
    action: '', // Unused
  },
};

interface ButtonProps {
  children: React.ReactNode;
  color?: Colors;
  variant?: Variants;
  padding?: keyof typeof PADDINGS;
  width?: 'auto' | 'full';
  isLoading?: boolean;
}

const Button = (
  props: ButtonProps &
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonProps>
) => {
  const {
    children,
    variant = 'basic',
    color = 'primary',
    padding = 'default',
    className,
    width = 'auto',
  } = props;
  return (
    <button
      {...Object.fromEntries(
        Object.entries(props).filter((e) => e[0] !== 'isLoading')
      )}
      className={clsx('outline-none', VARIANTS_COLORS[variant][color], {
        [PADDINGS_FULL_WIDTH[padding]]: width === 'full',
        [PADDINGS[padding]]: width === 'auto',
        [`${DISABLED_COLORS[color]} pointer-events-none cursor-not-allowed !border-transparent`]:
          props.disabled,
      })}
    >
      <div className={clsx(className, 'relative')}>
        {props.isLoading && (
          <div className={clsx('absolute z-[1] h-full w-full ')}>
            <Loader />
          </div>
        )}
        <div className={clsx(props.isLoading && 'invisible')}>{children}</div>
      </div>
    </button>
  );
};

export default Button;
